import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  TrendingUp, Search, Loader2, Printer, CheckCircle, Lock, Trash2, Send, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { printReport, downloadFile, sortRows } from '../lib/printReport';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);
const h = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const STATUS_BADGE = {
  pending_verification: 'bg-amber-100 text-amber-700',
  verified: 'bg-blue-100 text-blue-700',
  finalized: 'bg-green-100 text-green-700',
};

const nextMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 7);
};

/* ---------- Staff revision tab (breakdown or consolidated) ---------- */
function StaffRevisionTab({ salaryType, selectedEntity, onSaved }) {
  const isBreakdown = salaryType === 'breakdown';
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState({});
  const [inputs, setInputs] = useState({});
  const [effectiveMonth, setEffectiveMonth] = useState(nextMonth());
  const [bulkBasicPct, setBulkBasicPct] = useState('');
  const [bulkAgpAdd, setBulkAgpAdd] = useState('');
  const [bulkPct, setBulkPct] = useState('');

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ salary_type: salaryType });
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      const res = await fetch(`${API_URL}/api/salary-revisions/staff?${params}`, { headers: h(), credentials: 'include' });
      if (res.ok) setStaff(await res.json());
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  }, [salaryType, selectedEntity]);

  useEffect(() => { fetchStaff(); setSelected({}); setInputs({}); }, [fetchStaff]);

  const departments = [...new Set(staff.map(s => s.department).filter(Boolean))].sort();
  const categories = [...new Set(staff.map(s => s.category).filter(Boolean))].sort();
  const rows = staff.filter(s => {
    if (dept !== 'all' && s.department !== dept) return false;
    if (category !== 'all' && s.category !== category) return false;
    const q = search.toLowerCase();
    return !q || (s.employee_name || '').toLowerCase().includes(q) || (s.employee_id || '').toLowerCase().includes(q);
  });

  const selCount = rows.filter(r => selected[r.employee_id]).length;
  const toggleAll = (v) => {
    const next = { ...selected };
    rows.forEach(r => { if (!r.has_active_revision) next[r.employee_id] = v; });
    setSelected(next);
  };
  const setInput = (id, field, val) =>
    setInputs(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));

  const applyBulk = () => {
    const next = { ...inputs };
    rows.forEach(r => {
      if (!selected[r.employee_id] || r.has_active_revision) return;
      if (isBreakdown) {
        next[r.employee_id] = { basic_pct: bulkBasicPct, agp_add: bulkAgpAdd };
      } else if (bulkPct !== '') {
        next[r.employee_id] = { revised: String(Math.round(r.consolidated_salary * (1 + parseFloat(bulkPct) / 100))) };
      }
    });
    setInputs(next);
    toast.success(`Filled ${selCount} selected row(s)`);
  };

  const newBasic = (r) => {
    const p = parseFloat(inputs[r.employee_id]?.basic_pct || 0);
    return Math.round(r.basic_salary * (1 + p / 100));
  };
  const newAgp = (r) => Math.round(r.agp + parseFloat(inputs[r.employee_id]?.agp_add || 0));

  const save = async () => {
    const revisions = rows.filter(r => selected[r.employee_id] && !r.has_active_revision).map(r => {
      const inp = inputs[r.employee_id] || {};
      return isBreakdown
        ? { employee_id: r.employee_id, basic_pct: parseFloat(inp.basic_pct || 0), agp_add: parseFloat(inp.agp_add || 0) }
        : { employee_id: r.employee_id, revised_consolidated: parseFloat(inp.revised || 0) };
    }).filter(r => isBreakdown ? (r.basic_pct !== 0 || r.agp_add !== 0) : r.revised_consolidated > 0);
    if (!revisions.length) { toast.error('Select staff and enter revision values first'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-revisions/bulk`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ effective_month: effectiveMonth, revisions })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to save');
      toast.success(`${d.created} revision(s) created — pending verification`);
      if (d.skipped?.length) toast.warning(`${d.skipped.length} skipped: ${d.skipped[0].reason}`);
      setSelected({}); setInputs({});
      fetchStaff(); onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handlePrint = () => printReport(
    `Salary Revision Worksheet — ${isBreakdown ? 'Breakdown' : 'Consolidated'} Staff`,
    `Effective ${effectiveMonth} · ${rows.length} staff`,
    isBreakdown
      ? ['#', 'Employee', 'Dept', 'Category', 'Current Basic', 'Basic %', 'New Basic', 'Current AGP', 'AGP +', 'New AGP']
      : ['#', 'Employee', 'Dept', 'Category', 'Current Salary', 'Revised Salary'],
    rows.map((r, i) => isBreakdown
      ? [i + 1, `${r.employee_name} (${r.employee_id})`, r.department, r.category, fmt(r.basic_salary),
         inputs[r.employee_id]?.basic_pct || '', fmt(newBasic(r)), fmt(r.agp), inputs[r.employee_id]?.agp_add || '', fmt(newAgp(r))]
      : [i + 1, `${r.employee_name} (${r.employee_id})`, r.department, r.category, fmt(r.consolidated_salary),
         inputs[r.employee_id]?.revised ? fmt(inputs[r.employee_id].revised) : ''])
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="relative col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search staff..." className="pl-10" value={search}
            onChange={e => setSearch(e.target.value)} data-testid={`sr-search-${salaryType}`} />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger data-testid={`sr-dept-${salaryType}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid={`sr-cat-${salaryType}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={handlePrint} data-testid={`sr-print-${salaryType}`}>
          <Printer className="w-4 h-4 mr-1.5" />Print
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-slate-50 border rounded-lg p-3">
        <div className="space-y-1">
          <Label className="text-xs">Effective From (month)</Label>
          <Input type="month" className="w-40" value={effectiveMonth}
            onChange={e => setEffectiveMonth(e.target.value)} data-testid={`sr-eff-month-${salaryType}`} />
        </div>
        {isBreakdown ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs">Bulk: Basic increase %</Label>
              <Input type="number" step="0.5" className="w-28" placeholder="e.g. 5" value={bulkBasicPct}
                onChange={e => setBulkBasicPct(e.target.value)} data-testid="sr-bulk-basic-pct" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bulk: AGP + value (₹)</Label>
              <Input type="number" className="w-28" placeholder="e.g. 1000" value={bulkAgpAdd}
                onChange={e => setBulkAgpAdd(e.target.value)} data-testid="sr-bulk-agp-add" />
            </div>
          </>
        ) : (
          <div className="space-y-1">
            <Label className="text-xs">Bulk: increase %</Label>
            <Input type="number" step="0.5" className="w-28" placeholder="e.g. 10" value={bulkPct}
              onChange={e => setBulkPct(e.target.value)} data-testid="sr-bulk-pct" />
          </div>
        )}
        <Button variant="outline" size="sm" onClick={applyBulk} disabled={selCount === 0} data-testid={`sr-apply-bulk-${salaryType}`}>
          Fill {selCount} selected
        </Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto" onClick={save}
          disabled={saving || selCount === 0} data-testid={`sr-save-${salaryType}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
          Save {selCount} for Verification
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox checked={rows.length > 0 && rows.every(r => selected[r.employee_id] || r.has_active_revision)}
                        onCheckedChange={toggleAll} data-testid={`sr-select-all-${salaryType}`} />
                    </TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Dept / Category</TableHead>
                    {isBreakdown ? (
                      <>
                        <TableHead className="text-center">Current Basic</TableHead>
                        <TableHead className="text-center">Basic %</TableHead>
                        <TableHead className="text-center">New Basic</TableHead>
                        <TableHead className="text-center">Current AGP</TableHead>
                        <TableHead className="text-center">AGP + (₹)</TableHead>
                        <TableHead className="text-center">New AGP</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-center">Current Salary</TableHead>
                        <TableHead className="text-center">Revised Salary (₹)</TableHead>
                        <TableHead className="text-center">Change</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.employee_id} className={r.has_active_revision ? 'opacity-50 bg-slate-50' : selected[r.employee_id] ? 'bg-emerald-50/50' : ''}
                      data-testid={`sr-row-${r.employee_id}`}>
                      <TableCell>
                        <Checkbox checked={!!selected[r.employee_id]} disabled={r.has_active_revision}
                          onCheckedChange={v => setSelected(p => ({ ...p, [r.employee_id]: v }))}
                          data-testid={`sr-check-${r.employee_id}`} />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                        {r.has_active_revision && <Badge className="bg-amber-100 text-amber-700 text-[9px] ml-1">Revision in progress</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{r.department || '—'}<p className="text-[10px] text-slate-400">{r.category}</p></TableCell>
                      {isBreakdown ? (
                        <>
                          <TableCell className="text-center text-sm">₹{fmt(r.basic_salary)}</TableCell>
                          <TableCell className="text-center">
                            <Input type="number" step="0.5" className="h-8 w-20 text-xs text-center mx-auto" placeholder="%"
                              value={inputs[r.employee_id]?.basic_pct ?? ''} disabled={r.has_active_revision}
                              onChange={e => setInput(r.employee_id, 'basic_pct', e.target.value)}
                              data-testid={`sr-basic-pct-${r.employee_id}`} />
                          </TableCell>
                          <TableCell className="text-center text-sm font-semibold text-emerald-700">₹{fmt(newBasic(r))}</TableCell>
                          <TableCell className="text-center text-sm">₹{fmt(r.agp)}</TableCell>
                          <TableCell className="text-center">
                            <Input type="number" className="h-8 w-24 text-xs text-center mx-auto" placeholder="₹"
                              value={inputs[r.employee_id]?.agp_add ?? ''} disabled={r.has_active_revision}
                              onChange={e => setInput(r.employee_id, 'agp_add', e.target.value)}
                              data-testid={`sr-agp-add-${r.employee_id}`} />
                          </TableCell>
                          <TableCell className="text-center text-sm font-semibold text-emerald-700">₹{fmt(newAgp(r))}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-center text-sm">₹{fmt(r.consolidated_salary)}</TableCell>
                          <TableCell className="text-center">
                            <Input type="number" className="h-8 w-28 text-xs text-center mx-auto" placeholder="new ₹"
                              value={inputs[r.employee_id]?.revised ?? ''} disabled={r.has_active_revision}
                              onChange={e => setInput(r.employee_id, 'revised', e.target.value)}
                              data-testid={`sr-revised-${r.employee_id}`} />
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {inputs[r.employee_id]?.revised
                              ? <span className={parseFloat(inputs[r.employee_id].revised) >= r.consolidated_salary ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  {parseFloat(inputs[r.employee_id].revised) >= r.consolidated_salary ? '+' : ''}₹{fmt(parseFloat(inputs[r.employee_id].revised) - r.consolidated_salary)}
                                </span>
                              : '—'}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={isBreakdown ? 9 : 6} className="text-center py-10 text-slate-500 text-sm">No {salaryType} staff found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ---------- Revisions list: verify → finalize ---------- */
function RevisionsTab({ selectedEntity, refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [selected, setSelected] = useState({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      if (status !== 'all') params.append('status', status);
      const res = await fetch(`${API_URL}/api/salary-revisions?${params}`, { headers: h(), credentials: 'include' });
      if (res.ok) setRows(await res.json());
    } catch { toast.error('Failed to load revisions'); }
    finally { setLoading(false); }
  }, [selectedEntity, status, refreshKey]);

  useEffect(() => { fetchRows(); setSelected({}); }, [fetchRows]);

  const filtered = sortRows(rows.filter(r => {
    const q = search.toLowerCase();
    return !q || (r.employee_name || '').toLowerCase().includes(q) || (r.employee_id || '').toLowerCase().includes(q);
  }), sort, 'created_at');
  const selIds = filtered.filter(r => selected[r.revision_id]).map(r => r.revision_id);

  const handleExcel = async () => {
    try {
      const params = new URLSearchParams({ format: 'excel', sort });
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      if (status !== 'all') params.append('status', status);
      await downloadFile(`${API_URL}/api/salary-revisions?${params}`, 'salary_revisions.xlsx', h());
    } catch (e) { toast.error(e.message); }
  };

  const act = async (endpoint, label) => {
    if (!selIds.length) { toast.error('Select revisions first'); return; }
    setActing(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-revisions/${endpoint}`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ revision_ids: selIds })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || `Failed to ${label}`);
      toast.success(d.message);
      fetchRows(); onChanged && onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setActing(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this revision?')) return;
    try {
      const res = await fetch(`${API_URL}/api/salary-revisions/${id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      fetchRows(); onChanged && onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const changeText = (r) => r.salary_type === 'consolidated'
    ? `₹${fmt(r.old_consolidated)} → ₹${fmt(r.new_consolidated)}`
    : `Basic ₹${fmt(r.old_basic)} → ₹${fmt(r.new_basic)} (${r.basic_pct || 0}%) · AGP ₹${fmt(r.old_agp)} → ₹${fmt(r.new_agp)} (+₹${fmt(r.agp_add || 0)})`;

  const handlePrint = () => printReport(
    'Salary Revisions Report',
    `Status: ${status} · ${filtered.length} revision(s)`,
    ['#', 'Employee', 'Dept', 'Category', 'Type', 'Revision', 'Effective From', 'Status', 'Proposed By', 'Verified By', 'Finalized By'],
    filtered.map((r, i) => [i + 1, `${r.employee_name} (${r.employee_id})`, r.department, r.category,
      r.salary_type, changeText(r), r.effective_month, r.status?.replace('_', ' '),
      r.proposed_by_name || '', r.verified_by_name || '', r.finalized_by_name || ''])
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search employee..." className="pl-10" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="rev-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-52" data-testid="rev-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending_verification">Pending Verification</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="finalized">Finalized</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full md:w-44" data-testid="rev-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Date (newest first)</SelectItem>
            <SelectItem value="date_asc">Date (oldest first)</SelectItem>
            <SelectItem value="name_asc">Name (A–Z)</SelectItem>
            <SelectItem value="name_desc">Name (Z–A)</SelectItem>
            <SelectItem value="department">Department</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={handleExcel} data-testid="rev-excel-btn">
          <Download className="w-4 h-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" onClick={handlePrint} data-testid="rev-print-btn">
          <Printer className="w-4 h-4 mr-1.5" />Print
        </Button>
        <Button variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-50"
          onClick={() => act('verify', 'verify')} disabled={acting || !selIds.length} data-testid="rev-verify-btn">
          <CheckCircle className="w-4 h-4 mr-1.5" />Verify ({selIds.length})
        </Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={() => { if (window.confirm(`Finalize & LOCK ${selIds.length} revision(s)? Salary applies from each revision's effective month across the entire payroll system.`)) act('finalize', 'finalize'); }}
          disabled={acting || !selIds.length} data-testid="rev-finalize-btn">
          <Lock className="w-4 h-4 mr-1.5" />Finalize & Lock ({selIds.length})
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox checked={filtered.length > 0 && filtered.every(r => selected[r.revision_id] || r.status === 'finalized')}
                        onCheckedChange={v => {
                          const next = { ...selected };
                          filtered.forEach(r => { if (r.status !== 'finalized') next[r.revision_id] = v; });
                          setSelected(next);
                        }} data-testid="rev-select-all" />
                    </TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Dept / Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Revision</TableHead>
                    <TableHead className="text-center">Effective From</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.revision_id} data-testid={`rev-row-${r.revision_id}`}>
                      <TableCell>
                        <Checkbox checked={!!selected[r.revision_id]} disabled={r.status === 'finalized'}
                          onCheckedChange={v => setSelected(p => ({ ...p, [r.revision_id]: v }))}
                          data-testid={`rev-check-${r.revision_id}`} />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                      </TableCell>
                      <TableCell className="text-xs">{r.department || '—'}<p className="text-[10px] text-slate-400">{r.category}</p></TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] capitalize">{r.salary_type}</Badge></TableCell>
                      <TableCell className="text-xs">{changeText(r)}</TableCell>
                      <TableCell className="text-center text-xs font-medium">{r.effective_month}
                        {r.applied && <p className="text-[9px] text-emerald-600">applied ✓</p>}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded capitalize ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>
                          {(r.status || '').replace('_', ' ')}
                        </span>
                        {r.status === 'finalized' && <Lock className="w-3 h-3 inline ml-1 text-slate-400" />}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status !== 'finalized' && (
                          <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600 h-7 px-2"
                            onClick={() => del(r.revision_id)} data-testid={`rev-del-${r.revision_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500 text-sm">No revisions found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function SalaryRevision() {
  const { selectedEntity } = useOutletContext() || {};
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey(k => k + 1);

  return (
    <div className="space-y-6" data-testid="salary-revision-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-emerald-600" />Salary Revision
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Propose revisions (individually or in bulk) → Verify → Finalize & Lock. Finalized revisions apply from their effective month across the entire payroll system.
        </p>
      </div>
      <Tabs defaultValue="breakdown" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="breakdown" data-testid="tab-sr-breakdown">Breakdown Staff</TabsTrigger>
          <TabsTrigger value="consolidated" data-testid="tab-sr-consolidated">Consolidated Staff</TabsTrigger>
          <TabsTrigger value="revisions" data-testid="tab-sr-revisions">Verify & Finalize</TabsTrigger>
        </TabsList>
        <TabsContent value="breakdown" className="mt-4">
          <StaffRevisionTab salaryType="breakdown" selectedEntity={selectedEntity} onSaved={bump} />
        </TabsContent>
        <TabsContent value="consolidated" className="mt-4">
          <StaffRevisionTab salaryType="consolidated" selectedEntity={selectedEntity} onSaved={bump} />
        </TabsContent>
        <TabsContent value="revisions" className="mt-4">
          <RevisionsTab selectedEntity={selectedEntity} refreshKey={refreshKey} onChanged={bump} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
